<template>
  <div v-show="!hidden">
    <label for="page_icon" class="form-label">{{ $t("backend.candidacy.components.icon_selector.icon") }}</label>

    <div
      class="input-group rounded z-3 "
      :class="{
        'border-danger': !!currentValue && !iconExists,
        border: !!currentValue && !iconExists,
    }">
      <div
        class="input-group-text preview flex align-items-center justify-content-center"
        :class="{
          'bg-white': currentValue !== null && iconExists,
          'bg-error': currentValue && !iconExists,
      }">
        <AVIcon v-if="currentValue && iconExists" :icon="currentValue" />
        <AVIcon v-else-if="currentValue && !iconExists" icon="xmark" class="text-danger" />
      </div>

      <input
        :value="currentValue"
        class="form-control border-end-0"
        :list="`icon_options_${scopeToId(scope)}`"
        :id="scopeToId(scope)"
        :name="scopeToName(scope)"
        :placeholder="$t('backend.candidacy.components.icon_selector.placeholder')"
        @input="updateValue(($event.target as HTMLInputElement).value)"
      />

      <datalist :id="`icon_options_${scopeToId(scope)}`">
        <option v-for="icon in allIcons" :key="`filtered_icon_${icon}`" :value="icon" />
      </datalist>

      <div class="input-group-text bg-white p-0">
        <button
          class="btn border-0"
          type="button"
          data-bs-toggle="collapse"
          :data-bs-target="`#collapse_icons_${scopeToId(scope)}`"
          aria-expanded="false"
          :aria-controls="`collapse_icons_${scopeToId(scope)}`"
          @click="toggleCollapse"
        >
          <AVIcon icon="angle-down" class="small rotate" :class="{ open: isOpened }" />
        </button>
      </div>
    </div>

    <div class="collapse" :id="`collapse_icons_${scopeToId(scope)}`">
      <div
        class="collapse-card card rounded-bottom mt-n1 z-0 flex-row gap-1 px-1 pt-2 justify-content-between flex-wrap overflow-y-hidden border-top-0 bg-light"
      >
        <button
          v-for="qaIcon in quickAccessIcons"
          :key="`quick_access_icon_${qaIcon}`"
          class="btn btn-sm btn-outline-dark border-0"
          @click.prevent.stop="updateValue(qaIcon)"
        >
          <AVIcon :icon="qaIcon" />
        </button>
      </div>
    </div>

    <small v-if="!!currentValue && !iconExists" class="small text-danger mb-3">
      {{ $t("backend.candidacy.components.icon_selector.does_not_exist") }}
    </small>
  </div>
</template>

<script setup lang="ts">
import { iconNames } from "@assemblyvoting/electa-ui";
import { computed, onMounted, ref, watch } from "vue";
import type { PropType } from "@/types";
import { scopeToId, scopeToName } from "@/lib/candidacy/form_builder/utils";

type IconName = typeof iconNames[number];

const emits = defineEmits(["update:icon"]);

const props = defineProps({
  icon: {
    type: String as PropType<IconName>,
    default: null,
  },
  scope: {
    type: Array as PropType<string[]>,
    required: true,
  },
  hidden: {
    type: Boolean,
    default: false,
  },
});

// Exclude from suggestions icons that are being used in other places of the navigation menu.
const excludedIcons = ref<IconName[]>(["home", "users", "user", "user-plus", "circle-user", "user-circle", "user-pen", "address-card"]);

// Recommended icons to be shown on the dropdown.
// First icons on the array will have priority as this list is trimmed depending on screen size.
const quickAccessIcons = ref<IconName[]>([
  "calendar-days", "list", "newspaper", "chart-pie", "chart-simple", "chart-line", "ranking-star", "bars", "download", "upload", 
  "circle-info", "circle-question", "circle-exclamation", "triangle-exclamation", "location-dot", "map-location-dot", "folder-open", "folder", "file", "pen",
  "comment", "thumbtack", "briefcase", "link", "phone", "envelope", "shield-halved", "landmark", "landmark-dome", "landmark-flag", 
  "magnifying-glass", "lock", "passport", "id-card", "fingerprint", "arrow-up-from-bracket", "gears", "table", "rectangle-list", "envelope-open-text",
]);

const allIcons = computed(() => iconNames.filter((icon: IconName) => !excludedIcons.value.includes(icon) && icon));

const currentValue = ref<IconName>(null);

const iconExists = computed(() => iconNames.includes(currentValue.value));

const isOpened = ref<boolean>(false);

const updateValue = (newValue: IconName): void => {
  currentValue.value = newValue;
};

const toggleCollapse = () => isOpened.value = !isOpened.value;

onMounted(() => currentValue.value = props.icon)

watch(props, () => currentValue.value = props.icon);

watch(currentValue, () => {
  if (currentValue.value === "") currentValue.value = null
  emits("update:icon", currentValue.value);
});
</script>

<style scoped>
.preview {
  min-width: 2.5rem;
}

.bg-error {
  background-color: rgba(var(--bs-danger-rgb), 20%);
  border: none !important;
}

.collapse-card {
  max-height: 2.75rem;
  border-color: var(--bs-border-color);
}

.rotate {
  transition: all 300ms linear;
  transform: rotate(0);
}

.rotate.open {
  transform: rotate(180deg);
}
</style>
